import { GameEngine } from "./core/GameEngine";
import type { GameContext } from "./core/GameContext";
import { MoveEntityCommand } from "./core/commands/MoveEntityCommand";
import { NavigateCommand } from "./core/commands/NavigateCommand";
import { SystemGameClock } from "./core/GameClock";
import { AssetLoader } from "./assets/AssetLoader";
import type { AssetManifest } from "./assets/AssetManifest";
import { MusicManager } from "./audio/MusicManager";
import { SfxManager } from "./audio/SfxManager";
import { PixiRenderer } from "./rendering/PixiRenderer";
import { NativeJsonSceneImporter } from "./world/import/SceneImporter";
import { createBuiltinInteractionRegistry } from "./interaction/interactions";
import type { InteractionContext } from "./interaction/Interaction";
import { loadArtifactRegistry } from "./game-content/artifacts";
import { loadKnowledgeCatalog } from "./game-content/knowledge";
import { loadRecipeRegistry } from "./game-content/recipes";
import type { KnowledgeCatalog } from "./knowledge/KnowledgeGraph";
import { loadEntityDefinitionRegistry } from "./game-content/entity-definitions";
import { IndexedDbGameStorage } from "./persistence/IndexedDbGameStorage";
import { MemoryGameStorage } from "./persistence/MemoryGameStorage";
import type { GameStorage } from "./persistence/GameStorage";
import { WebPlatform } from "./platform/WebPlatform";
import { fetchJson } from "./platform/fetchJson";
import { DragController, type DropTarget } from "./input/DragController";
import { GamepadInput, GPButton } from "./input/GamepadInput";
import { getComponent, PLAYER_CHARACTER_ID, type Entity, type EntityId } from "./world/Entity";
import type { ClothingSlot } from "./world/components";
import { clamp, defaultTransform, rectAtBottomCenter, rectContains, type Vec2 } from "./world/geometry";
import { defaultPreferences, type Preferences } from "./persistence/GameSave";
import { extensions } from "pixi.js";
import { tiledMapLoader } from "pixi-tiledmap";
import { isWalkableAt, type ParsedTerrainGrid } from "./world/terrain";
import {
  createToast,
  createHelpOverlay,
  createKnowledgeOverlay,
  createOptionsOverlay,
  createGameMenuOverlay,
  createItemContextMenu,
  entityName,
  type GameMenuOverlay,
} from "./ui";
import { initMcp } from "./mcp";

const UNDRAGGABLE_KINDS = new Set(["furniture", "decoration"]);
const PLAYER_MOVE_SPEED = 260; // px/sec, ponytail: constant speed, no accel/friction
const WALK_ARRIVE_EPSILON = 2;

const MOVE_KEYS: Readonly<Record<string, Vec2>> = {
  arrowup: { x: 0, y: -1 },
  w: { x: 0, y: -1 },
  arrowdown: { x: 0, y: 1 },
  s: { x: 0, y: 1 },
  arrowleft: { x: -1, y: 0 },
  a: { x: -1, y: 0 },
  arrowright: { x: 1, y: 0 },
  d: { x: 1, y: 0 },
};

async function main(): Promise<void> {
  extensions.add(tiledMapLoader);

  const appContainer = document.getElementById("app");
  if (!appContainer) throw new Error("#app container missing");

  const [manifest, artifactsJson, knowledgeJson, entityIndex, recipesJson] = await Promise.all([
    fetchJson("/assets/manifest.json") as Promise<AssetManifest>,
    fetchJson("/content/artifacts.json"),
    fetchJson("/content/knowledge.json"),
    fetchJson("/content/entities/index.json") as Promise<string[]>,
    fetchJson("/content/recipes.json"),
  ]);

  const entityEntries = await Promise.all(
    entityIndex.map((id) => fetchJson(`/content/entities/${id}.json`)),
  );

  const assets = new AssetLoader(manifest);
  const artifacts = loadArtifactRegistry(artifactsJson);
  const knowledgeCatalog = loadKnowledgeCatalog(knowledgeJson);
  const entityDefinitions = loadEntityDefinitionRegistry(entityEntries);
  const recipes = loadRecipeRegistry(recipesJson);
  const interactions = createBuiltinInteractionRegistry();
  const drag = new DragController();

  let selectedInstanceId: string | undefined;
  let characterWalkArmed = false;
  let walkTarget: Vec2 | undefined;
  let onWalkSettled: (() => void) | undefined;
  let nearbyEntities = new Set<EntityId>();
  let openMenuEntityId: EntityId | undefined;
  const pressedMoveKeys = new Set<string>();
  const gamepad = new GamepadInput();

  function setWalkTarget(target: Vec2): void {
    onWalkSettled?.();
    onWalkSettled = undefined;
    walkTarget = target;
  }

  function clearWalkTarget(): void {
    walkTarget = undefined;
    onWalkSettled?.();
    onWalkSettled = undefined;
  }

  /** Walks the player to `target` at normal movement speed, resolving once they arrive or the walk is interrupted (keyboard input, a new walk target, or a scene change). */
  function walkPlayerTo(target: Vec2): Promise<Vec2> {
    const scene = engine.context.world.getCurrentScene();
    const clamped: Vec2 = { x: clamp(target.x, 0, scene.definition.size.width), y: clamp(target.y, 0, scene.definition.size.height) };
    return new Promise((resolve) => {
      setWalkTarget(clamped);
      onWalkSettled = () => resolve(currentEntity(PLAYER_CHARACTER_ID)?.transform.position ?? clamped);
    });
  }

  window.addEventListener("keydown", (e) => {
    const key = e.key.toLowerCase();
    if (!(key in MOVE_KEYS)) return;
    e.preventDefault();
    pressedMoveKeys.add(key); // updatePlayerMovement clears any queued walk-to-click once it sees real input
  });
  window.addEventListener("keyup", (e) => {
    pressedMoveKeys.delete(e.key.toLowerCase());
  });

  const renderer = await PixiRenderer.create(appContainer, assets, entityDefinitions, {
    onEntityPointerDown: (entityId, worldPosition) => onEntityPointerDown(entityId, worldPosition),
    onStagePointerMove: (worldPosition) => onStagePointerMove(worldPosition),
    onStagePointerUp: (worldPosition) => onStagePointerUp(worldPosition),
  });
  renderer.camera.focus(PLAYER_CHARACTER_ID);

  const storage: GameStorage = typeof indexedDB !== "undefined" ? new IndexedDbGameStorage() : new MemoryGameStorage();

  const STORAGE_KEY = "kurio:options";
  function loadPrefs(): Preferences {
    const defaults = window.matchMedia("(pointer: coarse)").matches
      ? { ...defaultPreferences(), mcpEnabled: false }
      : defaultPreferences();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch { /* ignore corrupt data */ }
    return defaults;
  }
  function savePrefs(prefs: Preferences): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }

  const sfx = new SfxManager(loadPrefs());

  const engine = new GameEngine({
    renderer,
    storage,
    assets,
    clock: new SystemGameClock(),
    platform: new WebPlatform(),
    knowledgeCatalog,
    artifacts,
    interactions,
    entityDefinitions,
    sceneImporter: new NativeJsonSceneImporter(),
    loadSceneJson: (sceneId) => fetchJson(`/worlds/${sceneId}.json`),
    sfx,
    recipes,
  });

  const hud = createHud(
    engine.context,
    manifest,
    knowledgeCatalog,
    storage,
    () => renderer.terrainGrid,
    (key, pressed) => pressed ? pressedMoveKeys.add(key) : pressedMoveKeys.delete(key),
  );
  engine.context.events.on("item:taken", () => hud.refresh());
  engine.context.events.on("item:dropped", () => hud.refresh());
  engine.context.events.on("character:dressed", () => hud.refresh());
  engine.context.events.on("character:undressed", () => hud.refresh());
  engine.context.events.on("artifact:discovered", (e) => {
    const definition = artifacts.get(e.artifactId);
    hud.toast(`Discovered: ${definition?.title ?? e.artifactId}!`);
  });
  engine.context.events.on("knowledge:unlocked", (e) => {
    const node = knowledgeCatalog.get(e.nodeId);
    hud.toast(`Learned: ${node?.title ?? e.nodeId}`);
    hud.refreshKnowledge();
  });
  engine.context.events.on("secret:revealed", () => hud.toast("You found something hidden!"));
  engine.context.events.on("entity:moved", (e) => {
    if (e.entityId === PLAYER_CHARACTER_ID) syncProximity(true);
  });
  engine.context.events.on("entity:approached", (e) => {
    const target = currentEntity(e.targetId);
    if (!target) return;
    const actions = engine.context.interactions.availableFor(target, { world: engine.context.world, commands: engine.context.commands, sfx: engine.context.sfx, artifacts: engine.context.artifacts });
    const defaultAction = actions.find((a) => a.isDefault);
    if (defaultAction) {
      void runInteraction(defaultAction.id, { entity: target });
    } else if (actions.length === 1) {
      void runInteraction(actions[0]!.id, { entity: target });
    } else if (actions.length > 1) {
      openActionsMenu(target, actions);
    }
  });
  engine.context.events.on("entity:left", (e) => {
    if (e.targetId === openMenuEntityId) {
      renderer.hideActionsMenu();
      openMenuEntityId = undefined;
    }
  });
  engine.context.events.on("scene:navigate", async (e) => {
    const player = currentEntity(PLAYER_CHARACTER_ID);
    pressedMoveKeys.clear();
    walkTarget = undefined; // stop movement now; resolve any pending move-to after the respawn below
    await engine.loadWorld(e.toScene);
    if (player) engine.context.world.addEntityToScene({ ...player, transform: defaultTransform(e.spawnPoint) });
    onSceneReady();
    clearWalkTarget(); // resolves with the correct post-navigation position instead of the stale old-scene one
  });
  engine.context.events.on("game:restart", () => {
    void storage.clear().then(() => location.reload());
  });

  hud.gameMenu.onSave(async () => {
    await storage.save(engine.context.world.toSave());
    const slots = await storage.listSlots();
    hud.gameMenu.setSaveList(slots);
  });
  hud.gameMenu.onLoad(async (slotId) => {
    const save = await storage.loadSlot(slotId);
    if (save) {
      engine.context.world.restore(save);
      const prefs = loadPrefs();
      engine.context.world.preferences = prefs;
      sfx.update(prefs);
      hud.syncPrefs(prefs);
      await engine.loadWorld(save.world.currentSceneId);
      onSceneReady();
      await storage.autoSave(save);
      hud.gameMenu.close();
      hud.refresh();
    }
  });
  hud.gameMenu.onDeleteSlot(async (slotId) => {
    await storage.deleteSlot(slotId);
    const slots = await storage.listSlots();
    hud.gameMenu.setSaveList(slots);
  });
  hud.gameMenu.onRestart(() => {
    storage.clear().then(() => location.reload());
  });

  hud.menuButton.onclick = () => {
    void storage.listSlots().then((slots) => {
      hud.gameMenu.setSaveList(slots);
      hud.gameMenu.toggle();
    });
  };

  hud.onInventorySelect((instanceId, anchor) => {
    const item = engine.context.world.inventory.items.find((i) => i.instanceId === instanceId);
    if (!item) return;
    const wearCheck: InteractionContext = { world: engine.context.world, commands: engine.context.commands, instanceId, actorId: PLAYER_CHARACTER_ID, artifacts: engine.context.artifacts };
    const actions: { id: string; label: string }[] = [];
    const wear = engine.context.interactions.get("wear");
    if (wear?.canExecute(wearCheck)) actions.push({ id: "wear", label: "Wear" });
    actions.push({ id: "drop", label: "Drop" });
    hud.showInventoryContext(item, actions, (id) => {
      if (id === "wear") {
        void runInteraction("wear", { actorId: PLAYER_CHARACTER_ID, instanceId });
      } else if (id === "drop") {
        selectedInstanceId = instanceId;
        hud.setSelectedInstance(instanceId);
        hud.toast("Click ground to drop");
      }
    }, anchor);
  });
  hud.onRemoveSlot((slot) => {
    void runInteraction("remove", { actorId: PLAYER_CHARACTER_ID, slot });
  });
  let mcpController: AbortController | undefined;
  hud.onPreferencesChange((prefs) => {
    engine.context.world.preferences = prefs;
    music.update(prefs);
    sfx.update(prefs);
    savePrefs(prefs);
    engine.context.commands.execute({ execute() {} });

    if (prefs.mcpEnabled && !mcpController) {
      mcpController = initMcp(engine, { walkTo: walkPlayerTo });
    } else if (!prefs.mcpEnabled && mcpController) {
      mcpController.abort();
      mcpController = undefined;
    }
  });

  const music = new MusicManager();

  // Autoload last save if available
  const lastSave = await storage.load();
  if (lastSave) {
    engine.context.world.restore(lastSave);
  }

  await engine.loadWorld(lastSave?.world.currentSceneId ?? "bedroom");
  onSceneReady();
  await engine.start();

  const prefs = loadPrefs();
  engine.context.world.preferences = prefs;
  music.start(prefs);
  sfx.update(prefs);
  hud.syncPrefs(prefs);
  if (prefs.mcpEnabled) mcpController = initMcp(engine, { walkTo: walkPlayerTo });
  window.addEventListener("pagehide", () => mcpController?.abort(), { once: true });

  window.addEventListener("gamepadconnected", (e) => {
    gamepad.onConnected(e.gamepad.index);
    hud.toast(`🎮 ${e.gamepad.id} connected — press any button`);
    hud.setGamepadStatus(true, e.gamepad.id);
  });
  window.addEventListener("gamepaddisconnected", (e) => {
    gamepad.onDisconnected(e.gamepad.index);
    hud.toast(`🎮 ${e.gamepad.id} disconnected`);
    hud.setGamepadStatus(false);
  });

  // Registered only after start() settles (scene guaranteed loaded, restore included) —
  // the ticker runs continuously from app.init(), so registering earlier risks a tick
  // landing mid-restore, when `world.restore()` has cleared scenes but not yet reloaded one.
  renderer.app.ticker.add((ticker) => {
    gamepad.poll();
    hud.handleGamepadInput(gamepad);
    if (gamepad.wasPressed(GPButton.A)) {
      void interactNearest();
    }
    updatePlayerMovement(ticker.deltaMS / 1000);
    hud.updateMinimap();
  });
  hud.refresh();
  hud.refreshKnowledge();

  async function runInteraction(id: string, partial: Partial<InteractionContext>): Promise<void> {
    const interaction = engine.context.interactions.get(id);
    if (!interaction) return;
    const ctx: InteractionContext = { world: engine.context.world, commands: engine.context.commands, sfx: engine.context.sfx, artifacts: engine.context.artifacts, ...partial };
    if (!interaction.canExecute(ctx)) return;
    const result = await interaction.execute(ctx);
    if (ctx.entity) {
      const soundable = getComponent(ctx.entity, "soundable");
      const sfxKey = soundable?.sounds[id];
      if (sfxKey) engine.context.sfx.play(sfxKey);
    }
    if (result.message) hud.toast(result.message);
    hud.refresh();
  }

  function currentEntity(id: EntityId): Entity | undefined {
    return engine.context.world.getEntity(id);
  }

  function snapCameraToPlayer(): void {
    const player = currentEntity(PLAYER_CHARACTER_ID);
    if (player) renderer.camera.panTo(player.transform.position);
  }

  /**
   * Recomputes which entities the player's ground anchor is inside.
   * Only entities newly inside that area fire "entity:approached" (emitEvents=true) —
   * this is what makes it an edge-triggered "comes closer" event rather than a level check,
   * so a portal the player is placed next to on scene entry (emitEvents=false, see
   * onSceneReady) doesn't immediately teleport them back until they actually leave and return.
   */
  function syncProximity(emitEvents: boolean): void {
    const player = currentEntity(PLAYER_CHARACTER_ID);
    const stillNear = new Set<EntityId>();
    if (player) {
      const scene = engine.context.world.getCurrentScene();
      const sceneId = engine.context.world.getCurrentSceneId();
      for (const ent of scene.entities) {
        if (ent.id === PLAYER_CHARACTER_ID) continue;
        const definition = engine.context.world.getEntityDefinition(ent.definitionId);
        if (!definition || !rectContains(rectAtBottomCenter(ent.transform.position, definition.size), player.transform.position)) continue;
        stillNear.add(ent.id);
        if (emitEvents && !nearbyEntities.has(ent.id)) {
          engine.context.events.emit("entity:approached", { entityId: PLAYER_CHARACTER_ID, targetId: ent.id, sceneId });
        }
      }
      if (emitEvents) {
        for (const id of nearbyEntities) {
          if (!stillNear.has(id)) {
            engine.context.events.emit("entity:left", { entityId: PLAYER_CHARACTER_ID, targetId: id, sceneId });
          }
        }
      }
    }
    nearbyEntities = stillNear;
  }

  /** Shows the actions menu for `entity`, tracking which entity owns it so "entity:left" can auto-close it. */
  function openActionsMenu(entity: Entity, actions: readonly { id: string; label: string }[]): void {
    openMenuEntityId = entity.id;
    renderer.showActionsMenu(entity.transform.position, entityName(entity.definitionId), actions, (id) => {
      openMenuEntityId = undefined;
      void runInteraction(id, { entity });
    });
  }

  function onSceneReady(): void {
    snapCameraToPlayer();
    syncProximity(false);
  }

  function updatePlayerMovement(deltaSeconds: number): void {
    const player = currentEntity(PLAYER_CHARACTER_ID);
    if (!player) return;
    const pos = player.transform.position;
    const maxStep = PLAYER_MOVE_SPEED * deltaSeconds;

    let keyDx = 0;
    let keyDy = 0;
    for (const key of pressedMoveKeys) {
      const d = MOVE_KEYS[key];
      if (d) {
        keyDx += d.x;
        keyDy += d.y;
      }
    }

    const gp = gamepad.current;
    if (gp.connected) {
      keyDx += gp.movement.x;
      keyDy += gp.movement.y;
    }

    let next: Vec2 | undefined;
    if (keyDx !== 0 || keyDy !== 0) {
      if (walkTarget) clearWalkTarget(); // real input (keyboard or gamepad) takes over from a queued walk-to
      const len = Math.hypot(keyDx, keyDy);
      next = { x: pos.x + (keyDx / len) * maxStep, y: pos.y + (keyDy / len) * maxStep };
    } else if (walkTarget) {
      const dx = walkTarget.x - pos.x;
      const dy = walkTarget.y - pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= WALK_ARRIVE_EPSILON) {
        next = walkTarget;
        clearWalkTarget();
      } else {
        const step = Math.min(maxStep, dist);
        next = { x: pos.x + (dx / dist) * step, y: pos.y + (dy / dist) * step };
      }
    }
    if (!next) return;

    const scene = engine.context.world.getCurrentScene();
    const clamped: Vec2 = {
      x: clamp(next.x, 0, scene.definition.size.width),
      y: clamp(next.y, 0, scene.definition.size.height),
    };
    // ponytail: full block on unwalkable tiles, no axis-sliding — diagonal input can stick on a wall corner.
    // Upgrade: retry x-only then y-only step here if that ever feels bad.
    if (!isWalkableAt(renderer.terrainGrid, clamped)) {
      if (walkTarget) clearWalkTarget();
      return;
    }
    engine.context.commands.execute(new MoveEntityCommand(PLAYER_CHARACTER_ID, clamped));
  }

  async function onEntityPointerDown(entityId: EntityId, worldPosition: Vec2): Promise<void> {
    const entity = currentEntity(entityId);
    if (!entity) return;

    const portal = getComponent(entity, "portal");
    if (portal) {
      const soundable = getComponent(entity, "soundable");
      if (soundable?.sounds["look"]) engine.context.sfx.play(soundable.sounds["look"]);
      onPortalClick(entityId);
      return;
    }

    if (entityId === PLAYER_CHARACTER_ID && selectedInstanceId) {
      // Clear before awaiting: the paired stage "pointerup" (fired right after this pointerdown,
      // before this await resolves) reads `selectedInstanceId` too — stale state there made it
      // try to drop the item this wear just consumed.
      const instanceId = selectedInstanceId;
      selectedInstanceId = undefined;
      hud.setSelectedInstance(undefined);
      await runInteraction("wear", { actorId: PLAYER_CHARACTER_ID, instanceId });
      return;
    }

    if (entityId === PLAYER_CHARACTER_ID) {
      characterWalkArmed = true;
      hud.toast("Click a spot to walk there");
    }

    const actions = engine.context.interactions.availableFor(entity, { world: engine.context.world, commands: engine.context.commands, sfx: engine.context.sfx, artifacts: engine.context.artifacts });
    if (actions.length === 1) {
      void runInteraction(actions[0]!.id, { entity });
    } else if (actions.length > 1) {
      openActionsMenu(entity, actions);
    }

    if (getComponent(entity, "draggable") && !UNDRAGGABLE_KINDS.has(entity.kind)) {
      drag.pointerDown(entityId, worldPosition);
    }
  }

  function onStagePointerMove(worldPosition: Vec2): void {
    const state = drag.pointerMove(worldPosition);
    if (state.phase === "dragging" && state.entityId) {
      const container = renderer.getViewContainer(state.entityId);
      if (container) container.position.set(worldPosition.x, worldPosition.y);
    }
  }

  async function onStagePointerUp(worldPosition: Vec2): Promise<void> {
    const scene = engine.context.world.getCurrentScene();
    const dropTargets: DropTarget[] = scene.entities
      .map((e) => {
        const surface = getComponent(e, "surface");
        if (!surface) return undefined;
        const bounds = {
          x: e.transform.position.x + surface.bounds.x,
          y: e.transform.position.y + surface.bounds.y,
          width: surface.bounds.width,
          height: surface.bounds.height,
        };
        return { id: e.id, bounds } satisfies DropTarget;
      })
      .filter((t): t is DropTarget => t !== undefined);

    const result = drag.pointerUp(dropTargets);
    if (!result) {
      if (selectedInstanceId) {
        await runInteraction("drop", { instanceId: selectedInstanceId, position: worldPosition });
        selectedInstanceId = undefined;
        hud.setSelectedInstance(undefined);
      } else if (characterWalkArmed) {
        setWalkTarget(worldPosition);
        characterWalkArmed = false;
      }
      return;
    }

    const draggedEntity = currentEntity(result.entityId);
    if (result.dropTarget && draggedEntity) {
      const surface = currentEntity(result.dropTarget.id);
      if (surface) {
        const localOffset: Vec2 = {
          x: result.position.x - surface.transform.position.x,
          y: result.position.y - surface.transform.position.y,
        };
        const ctx: InteractionContext = {
          world: engine.context.world,
          commands: engine.context.commands,
          sfx: engine.context.sfx,
          artifacts: engine.context.artifacts,
          entity: draggedEntity,
          surfaceEntityId: result.dropTarget.id,
          position: localOffset,
        };
        const place = engine.context.interactions.get("place");
        if (place?.canExecute(ctx)) {
          await place.execute(ctx);
          hud.refresh();
          return;
        }
      }
    }
    engine.context.commands.execute(new MoveEntityCommand(result.entityId, result.position));
  }

  function onPortalClick(portalEntityId: EntityId): void {
    engine.context.commands.execute(new NavigateCommand(portalEntityId));
  }

  async function interactNearest(): Promise<void> {
    const player = currentEntity(PLAYER_CHARACTER_ID);
    if (!player) return;
    const pos = player.transform.position;
    const scene = engine.context.world.getCurrentScene();
    const ctx = { world: engine.context.world, commands: engine.context.commands, sfx: engine.context.sfx, artifacts: engine.context.artifacts };
    let best: Entity | undefined;
    let bestDist = Infinity;
    for (const e of scene.entities) {
      if (e.id === PLAYER_CHARACTER_ID || !e.visible) continue;
      const actions = engine.context.interactions.availableFor(e, ctx);
      if (actions.length === 0) continue;
      const dx = e.transform.position.x - pos.x;
      const dy = e.transform.position.y - pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist < bestDist) {
        bestDist = dist;
        best = e;
      }
    }
    if (best && bestDist < 120) {
      await onEntityPointerDown(best.id, best.transform.position);
    }
  }
}

interface Hud {
  refresh(): void;
  refreshKnowledge(): void;
  updateMinimap(): void;
  toast(message: string): void;
  setSelectedInstance(instanceId: string | undefined): void;
  showActions(entity: Entity, actions: readonly { id: string; label: string }[], onPick: (id: string) => void): void;
  showInventoryContext(item: { instanceId: string; definitionId: string }, actions: readonly { id: string; label: string }[], onPick: (id: string) => void, anchor: HTMLElement): void;
  onInventorySelect(handler: (instanceId: string, anchor: HTMLElement) => void): void;
  onRemoveSlot(handler: (slot: ClothingSlot) => void): void;
  onPreferencesChange(handler: (prefs: Preferences) => void): void;
  syncPrefs(prefs: Preferences): void;
  setGamepadStatus(connected: boolean, name?: string): void;
  handleGamepadInput(gamepad: GamepadInput): void;
  readonly gameMenu: GameMenuOverlay;
  readonly menuButton: HTMLButtonElement;
}

const ACTION_TIPS: Readonly<Record<string, string>> = {
  look: "Read a description (no action taken)",
  inspect: "Reveal an artifact's true identity",
  examine: "Study a discovered artifact more closely",
  take: "Pick up into your backpack",
  drop: "Place on the ground at your feet",
  place: "Set down on a table or shelf",
  open: "Look inside a container",
  wear: "Put on as clothing",
  "wear-from-scene": "Put on directly — no backpack needed",
  remove: "Take off worn clothing",
  rub: "Clean a tarnished surface",
  combine: "Merge with an item from your backpack",
};

function resolveAsset(manifest: AssetManifest, assetKey: string): { kind: "emoji"; glyph: string } | { kind: "image"; src: string } | undefined {
  const entry = manifest[assetKey];
  if (!entry) return undefined;
  if (entry.startsWith("emoji:")) {
    const rest = entry.slice("emoji:".length);
    const sepIndex = rest.lastIndexOf(":");
    return sepIndex === -1 ? undefined : { kind: "emoji", glyph: rest.slice(0, sepIndex) };
  }
  return { kind: "image", src: `/assets/${entry}` };
}

function createHud(
  context: GameContext,
  manifest: AssetManifest,
  knowledgeCatalog: KnowledgeCatalog,
  storage: GameStorage,
  getTerrainGrid: () => ParsedTerrainGrid | undefined,
  onMoveKeyChange: (key: string, pressed: boolean) => void,
): Hud {
  const style = document.createElement("style");
  style.textContent = `
    #hud { position: fixed; inset: auto 0 0 0; display: flex; flex-direction: column; gap: 8px; padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); font: 16px system-ui, sans-serif; color: #314338; pointer-events: none; }
    #hud * { pointer-events: auto; }
    #hud .row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    #hud .toolbar { align-self: center; justify-content: space-evenly; width: min(100%, 860px); gap: 12px; }
    #hud .toolbar-icon { line-height: 1; }
    #hud .hint { align-self: flex-start; color: #fffdf5; font-size: 13px; text-shadow: 0 1px 3px #314338; transition: opacity .3s; }
    #hud .hint.hiding { opacity: 0; }
    #hud button { background: #fffdf5e8; color: #314338; border: 2px solid #fffdf5; border-radius: 999px; box-shadow: 0 3px 0 #42694b99; padding: 8px 14px; font-size: 15px; font-weight: 600; cursor: pointer; user-select: none; -webkit-user-select: none; -webkit-tap-highlight-color: transparent; }
    #hud button.selected { background: #79b889; color: #173420; border-color: #dff2d8; box-shadow: 0 3px 0 #42694b; }
    #hud button:hover { background: #e7f5db; transform: translateY(-1px); }
    .inv-item { display: inline-flex; align-items: center; gap: 5px; }
    .inv-item .inv-icon { font-size: 18px; line-height: 1; }
    .inv-item .inv-icon-img { width: 18px; height: 18px; object-fit: contain; }
    .inv-item .inv-label { font-size: 14px; }
    #backpack-panel { display: none; flex-direction: column; gap: 8px; }
    #backpack-panel.show { display: flex; background: #fffdf5f2; border: 2px solid #fffdf5; border-radius: 18px; box-shadow: 0 4px 12px #31433833; padding: 8px; }
    #backpack-panel .label { color: #42694b; font-size: 13px; font-weight: 600; }
    #touch-dpad { display: none; grid-template-columns: repeat(3, 52px); grid-template-rows: repeat(3, 52px); gap: 0; align-self: flex-start; }
    #touch-dpad button { width: 52px; height: 52px; padding: 0; font-size: 22px; touch-action: none; }
    #touch-dpad .up:hover, #touch-dpad .down:hover { transform: none; }
    #touch-dpad .up { grid-column: 2; }
    #touch-dpad .left, #touch-dpad .left:hover { grid-column: 1; grid-row: 2; transform: translateX(16px); }
    #touch-dpad .down { grid-column: 2; grid-row: 3; }
    #touch-dpad .right, #touch-dpad .right:hover { grid-column: 3; grid-row: 2; transform: translateX(-16px); }
    #minimap { position: fixed; top: 12px; right: 12px; background: #fffdf5bf; border: 2px solid #fffdf5; border-radius: 18px; box-shadow: 0 4px 12px #31433833; display: none; }
    #minimap.show { display: block; }
    #touch-dpad.show { display: grid; }
    @media (max-width: 600px) {
      #hud .toolbar { gap: 8px; }
      #hud .toolbar button { min-width: 44px; min-height: 44px; padding: 8px; }
      #hud .toolbar-label { display: none; }
      #hud .toolbar-icon { font-size: 20px; }
    }
  `;
  document.head.appendChild(style);

  const toast = createToast();
  const help = createHelpOverlay();
  const knowledge = createKnowledgeOverlay(knowledgeCatalog, context.knowledge, context.recipes);
  const options = createOptionsOverlay();
  const gameMenu = createGameMenuOverlay();
  const itemContext = createItemContextMenu();

  const root = document.createElement("div");
  root.id = "hud";
  const hint = document.createElement("div");
  hint.className = "hint";
  hint.textContent =
    "Click objects for actions • Drag to move • Backpack: items & clothing • Press ? for help";
  setTimeout(() => {
    hint.classList.add("hiding");
    setTimeout(() => { hint.hidden = true; }, 300);
  }, 8000);

  const carryingLabel = document.createElement("div");
  carryingLabel.className = "label";
  carryingLabel.textContent = "Carrying";
  const inventoryRow = document.createElement("div");
  inventoryRow.className = "row";
  const wearingLabel = document.createElement("div");
  wearingLabel.className = "label";
  wearingLabel.textContent = "Wearing";
  const clothingRow = document.createElement("div");
  clothingRow.className = "row";
  const backpackPanel = document.createElement("div");
  backpackPanel.id = "backpack-panel";
  backpackPanel.append(carryingLabel, inventoryRow, wearingLabel, clothingRow);

  const actionsRow = document.createElement("div");
  actionsRow.className = "row";

  const touchDpad = document.createElement("div");
  touchDpad.id = "touch-dpad";
  const createMoveButton = (key: string, icon: string, label: string): HTMLButtonElement => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = key.slice("arrow".length);
    button.textContent = icon;
    button.title = label;
    button.ariaLabel = label;
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      onMoveKeyChange(key, true);
    });
    const release = (event: PointerEvent) => {
      event.preventDefault();
      onMoveKeyChange(key, false);
    };
    button.addEventListener("pointerup", release);
    button.addEventListener("pointercancel", release);
    return button;
  };
  touchDpad.append(
    createMoveButton("arrowup", "▲", "Move up"),
    createMoveButton("arrowleft", "◀", "Move left"),
    createMoveButton("arrowdown", "▼", "Move down"),
    createMoveButton("arrowright", "▶", "Move right"),
  );

  const toolbarRow = document.createElement("div");
  toolbarRow.className = "row toolbar";
  const createToolbarButton = (icon: string, label: string): HTMLButtonElement => {
    const button = document.createElement("button");
    button.title = label;
    button.ariaLabel = label;
    const iconEl = document.createElement("span");
    iconEl.className = "toolbar-icon";
    iconEl.ariaHidden = "true";
    iconEl.textContent = icon;
    const labelEl = document.createElement("span");
    labelEl.className = "toolbar-label";
    labelEl.textContent = ` ${label}`;
    button.append(iconEl, labelEl);
    return button;
  };
  const backpackToggle = createToolbarButton("🎒", "Backpack");
  const mapToggle = createToolbarButton("🗺", "Map");
  const helpButton = createToolbarButton("?", "Help");
  const optionsButton = createToolbarButton("⚙", "Options");
  const menuButton = createToolbarButton("☰", "Menu");
  const knowledgeButton = createToolbarButton("📚", "Knowledge");
  toolbarRow.append(backpackToggle, mapToggle, knowledgeButton, helpButton, optionsButton, menuButton);

  root.append(hint, actionsRow, backpackPanel, touchDpad, toolbarRow);
  document.body.appendChild(root);

  const minimapCanvas = document.createElement("canvas");
  minimapCanvas.id = "minimap";
  minimapCanvas.width = 180;
  minimapCanvas.height = 135;
  document.body.appendChild(minimapCanvas);
  let minimapVisible = false;

  function setTouchControlsVisible(visible: boolean): void {
    touchDpad.classList.toggle("show", visible);
  }

  helpButton.onclick = () => help.toggle();
  optionsButton.onclick = () => options.toggle();
  knowledgeButton.onclick = () => knowledge.toggle();

  options.syncPrefs(context.world.preferences);
  options.onPreferencesChange((prefs) => {
    setTouchControlsVisible(prefs.touchControlsEnabled);
    preferencesChangeHandler?.(prefs);
  });

  window.addEventListener("keydown", (e) => {
    if (e.key === "?" || (e.key === "/" && !e.ctrlKey && !e.metaKey)) {
      if (document.activeElement === document.body || document.activeElement === document.documentElement) {
        e.preventDefault();
        help.toggle();
      }
    }
    if (e.key === "k" && !e.ctrlKey && !e.metaKey) {
      if (document.activeElement === document.body || document.activeElement === document.documentElement) {
        e.preventDefault();
        knowledge.toggle();
      }
    }
    if (e.key === "m" && !e.ctrlKey && !e.metaKey) {
      if (document.activeElement === document.body || document.activeElement === document.documentElement) {
        e.preventDefault();
        void storage.listSlots().then((slots) => {
          gameMenu.setSaveList(slots);
          gameMenu.toggle();
        });
      }
    }
    if (e.key === "o" && !e.ctrlKey && !e.metaKey) {
      if (document.activeElement === document.body || document.activeElement === document.documentElement) {
        e.preventDefault();
        options.toggle();
      }
    }
    if (e.key === "Escape") {
      if (gameMenu.confirmElement.classList.contains("show")) {
        gameMenu.closeConfirm();
      } else if (gameMenu.menuElement.classList.contains("show")) {
        gameMenu.close();
      } else if (knowledge.element.classList.contains("show")) {
        knowledge.close();
      } else if (options.element.classList.contains("show")) {
        options.close();
      } else if (help.element.classList.contains("show")) {
        help.close();
      }
    }
  });

  backpackToggle.onclick = () => {
    const visible = !backpackPanel.classList.contains("show");
    backpackPanel.classList.toggle("show", visible);
    backpackToggle.classList.toggle("selected", visible);
  };
  mapToggle.onclick = () => {
    toggleMinimap();
  };

  let inventorySelectHandler: ((instanceId: string, anchor: HTMLElement) => void) | undefined;
  let removeSlotHandler: ((slot: ClothingSlot) => void) | undefined;
  let preferencesChangeHandler: ((prefs: Preferences) => void) | undefined;
  let selected: string | undefined;

  function setMinimapVisible(visible: boolean): void {
    minimapVisible = visible;
    minimapCanvas.classList.toggle("show", visible);
    mapToggle.classList.toggle("selected", visible);
  }

  function toggleMinimap(): void {
    setMinimapVisible(!minimapVisible);
    preferencesChangeHandler?.({ ...context.world.preferences, minimapVisible });
  }

  function resolveItemIcon(definitionId: string): { kind: "emoji"; glyph: string } | { kind: "image"; src: string } {
    const direct = resolveAsset(manifest, definitionId);
    if (direct) return direct;
    const artifact = context.artifacts.get(definitionId);
    if (artifact) {
      const asset = resolveAsset(manifest, artifact.asset);
      if (asset) return asset;
    }
    return { kind: "emoji", glyph: "?" };
  }

  function refresh(): void {
    inventoryRow.innerHTML = "";
    for (const item of context.world.inventory.items) {
      const button = document.createElement("button");
      const icon = resolveItemIcon(item.definitionId);
      const name = item.definitionId.split(".").pop() ?? item.definitionId;
      const qty = item.quantity > 1 ? ` x${item.quantity}` : "";
      const iconHtml = icon.kind === "emoji"
        ? `<span class="inv-icon">${icon.glyph}</span>`
        : `<img class="inv-icon-img" src="${icon.src}" alt="" />`;
      button.innerHTML = `<span class="inv-item">${iconHtml}<span class="inv-label">${name}${qty}</span></span>`;
      if (item.instanceId === selected) button.classList.add("selected");
      button.onclick = () => inventorySelectHandler?.(item.instanceId, button);
      inventoryRow.appendChild(button);
    }

    clothingRow.innerHTML = "";
    const player = context.world.getEntity(PLAYER_CHARACTER_ID);
    const character = player && getComponent(player, "character");
    if (character) {
      for (const [slot, definitionId] of Object.entries(character.clothing)) {
        if (!definitionId) continue;
        const button = document.createElement("button");
        const icon = resolveItemIcon(definitionId);
        const name = definitionId.split(".").pop() ?? definitionId;
        const iconHtml = icon.kind === "emoji"
          ? `<span class="inv-icon">${icon.glyph}</span>`
          : `<img class="inv-icon-img" src="${icon.src}" alt="" />`;
        button.innerHTML = `<span class="inv-item">${iconHtml}<span class="inv-label">${slot}: ${name}</span></span>`;
        button.onclick = () => removeSlotHandler?.(slot as ClothingSlot);
        clothingRow.appendChild(button);
      }
    }
  }

  function updateMinimap(): void {
    if (!minimapVisible) return;
    const ctx = minimapCanvas.getContext("2d");
    if (!ctx) return;
    const scene = context.world.getCurrentScene();
    const { width, height } = scene.definition.size;
    ctx.clearRect(0, 0, minimapCanvas.width, minimapCanvas.height);

    ctx.globalAlpha = 0.75;
    const parsed = getTerrainGrid();
    if (parsed) {
      const tileW = minimapCanvas.width / parsed.cols;
      const tileH = minimapCanvas.height / parsed.rows;
      for (let row = 0; row < parsed.rows; row++) {
        for (let col = 0; col < parsed.cols; col++) {
          const idx = parsed.grid[row]![col]!;
          if (idx === 0) continue;
          const key = parsed.paletteKeys[idx - 1];
          const color = key ? parsed.paletteColors[key] : undefined;
          if (!color) continue;
          ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
          ctx.fillRect(col * tileW, row * tileH, Math.ceil(tileW), Math.ceil(tileH));
        }
      }
    }
    ctx.globalAlpha = 1;

    const scaleX = minimapCanvas.width / width;
    const scaleY = minimapCanvas.height / height;
    for (const entity of scene.entities) {
      if (!entity.visible) continue;
      const isPlayer = entity.id === PLAYER_CHARACTER_ID;
      ctx.fillStyle = isPlayer ? "#ffcc33" : "#8a4fd6";
      ctx.beginPath();
      ctx.arc(entity.transform.position.x * scaleX, entity.transform.position.y * scaleY, isPlayer ? 4 : 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return {
    refresh,
    refreshKnowledge: () => knowledge.refresh(),
    updateMinimap,
    toast: (message) => toast.show(message),
    setSelectedInstance(instanceId) {
      selected = instanceId;
      refresh();
    },
    showActions(entity, actions, onPick) {
      actionsRow.innerHTML = "";
      const label = document.createElement("span");
      label.textContent = `${entity.id}:`;
      actionsRow.appendChild(label);
      for (const action of actions) {
        const button = document.createElement("button");
        button.textContent = action.label;
        button.title = ACTION_TIPS[action.id] ?? action.label;
        button.onclick = () => onPick(action.id);
        actionsRow.appendChild(button);
      }
    },
    showInventoryContext: (item, actions, onPick, anchor) => itemContext.show(item, actions, onPick, anchor),
    onInventorySelect(handler) {
      inventorySelectHandler = handler;
    },
    onRemoveSlot(handler) {
      removeSlotHandler = handler;
    },
    onPreferencesChange(handler) {
      preferencesChangeHandler = handler;
    },
    syncPrefs(prefs) {
      options.syncPrefs(prefs);
      setMinimapVisible(prefs.minimapVisible);
      setTouchControlsVisible(prefs.touchControlsEnabled);
    },
    setGamepadStatus: (connected, name) => options.setGamepadStatus(connected, name),
    handleGamepadInput(gp) {
      if (!gp.connected) return;
      if (gp.wasPressed(GPButton.B)) {
        if (gameMenu.confirmElement.classList.contains("show")) {
          gameMenu.closeConfirm();
        } else if (gameMenu.menuElement.classList.contains("show")) {
          gameMenu.close();
        } else if (knowledge.element.classList.contains("show")) {
          knowledge.close();
        } else if (options.element.classList.contains("show")) {
          options.close();
        } else if (help.element.classList.contains("show")) {
          help.close();
        }
      }
      if (gp.wasPressed(GPButton.X)) {
        const visible = !backpackPanel.classList.contains("show");
        backpackPanel.classList.toggle("show", visible);
        backpackToggle.classList.toggle("selected", visible);
      }
      if (gp.wasPressed(GPButton.Start)) {
        gameMenu.toggle();
      }
      if (gp.wasPressed(GPButton.Capture)) {
        toggleMinimap();
      }
    },
    gameMenu,
    menuButton,
  };
}

main().catch((error: unknown) => {
  console.error("[kurio] failed to start", error);
});
